import { Widget } from "../lib/widget"
import { $ } from "../lib/dom"

function createBoxPreview() {
  const Preview = window.Box?.Preview || window.Box?.ContentPreview
  return Preview ? new Preview() : null
}

class DocumentView extends Widget {
  constructor(options = {}) {
    super()
    this.fileId = options.fileId
    this.uploadToken = options.uploadToken
    this.previewToken = options.previewToken
    this.previewOptions = {
      container: "#document-container",
      header: "none",
      showAnnotations: false,
      showDownload: false,
      ...(options.previewOptions || {})
    }
    this.container = this.previewOptions.container
    this.containerHTML = this.$(this.container).html()
    this.preview = createBoxPreview()
    this.setSubmission(options.submission)
    this.bindEvents()
    if (this.fileId) this.showPreview()
  }

  bindEvents() {
    this.delegate("click", "#upload-button", this.triggerFileDialog)
    this.delegate("click", "#submit-url-button", this.submitUrl)
    this.delegate("change", "#file-input", this.verifyFile)
    this.delegate("click", "#update-document", this.resetUploader)
    this.delegate("click", "#download-document", this.downloadDocument)
    this.delegate("paste", "#submission-form textarea", this.cleanInput)
  }

  setSubmission(submission) {
    this.submission = submission
    if (!submission) return
    this.fileId = submission.get("box_file_id")
    this.url = submission.get("url")
    ;["change:id", "change:box_file_id", "change:url"].forEach((event) => {
      submission.on(event, this.updateSubmissionState, this)
    })
  }

  setFileId(fileId) {
    this.fileId = fileId
    if (fileId) this.showPreview()
  }

  triggerFileDialog(event) {
    event.preventDefault()
    this.$("#file-input").trigger("click")
  }

  verifyFile(event, input) {
    const file = input.files?.[0]
    if (!file) return
    const types = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.oasis.opendocument.text",
      "text/plain"
    ]
    if (!types.includes(file.type)) {
      this.showError("Unsupported format. Please choose a pdf, doc, docx, odt or txt file type.")
    } else if (file.size > 26_214_400) {
      this.showError("File size too large. Please select a document under 25MB.")
    } else {
      this.uploadDocument()
    }
  }

  async submitUrl(event) {
    event.preventDefault()
    const submissionUrl = String(this.$("#submission_url").val() || "").trim()
    if (!this.isValidUrl(submissionUrl)) return this.showError("Invalid URL. Please enter a valid URL.")
    if (!this.isValidGoogleUrl(submissionUrl)) return this.showError("Only Google docs URLs are supported. Please share a valid docs.google.com link.")

    this.setLoading(true)
    try {
      await this.submission.save({ url: submissionUrl })
      this.updateSubmissionState()
    } catch (_) {
      this.showError("Error saving submission. Please try again.")
    } finally {
      this.setLoading(false)
    }
  }

  isValidUrl(value) {
    try {
      const url = new URL(value.startsWith("http") ? value : `https://${value}`)
      return ["http:", "https:"].includes(url.protocol) && Boolean(url.hostname)
    } catch (_) {
      return false
    }
  }

  isValidGoogleUrl(value) {
    try {
      const url = new URL(value.startsWith("http") ? value : `https://${value}`)
      return url.hostname === "docs.google.com"
    } catch (_) {
      return false
    }
  }

  async uploadDocument() {
    this.fileId = undefined
    this.$("#upload-error").hide()
    this.$("#upload-button").prop("disabled", true).text("Please wait...")
    this.$("#upload-progress").show()

    try {
      const form = document.querySelector("#document-form")
      const response = await fetch("https://upload.box.com/api/2.0/files/content", {
        method: "POST",
        headers: { Authorization: `Bearer ${this.uploadToken}` },
        body: new FormData(form)
      })
      if (!response.ok) throw new Error(`Upload failed (${response.status})`)
      const payload = await response.json()
      this.fileId = payload.entries?.[0]?.id
      if (!this.fileId) throw new Error("Box did not return a file id")
      if (this.submission) await this.submission.save({ box_file_id: this.fileId })
      this.showPreview()
    } catch (_) {
      this.showError("There was a problem uploading your document. Please refresh the page and try again.")
    }
  }

  showPreview() {
    this.$(".loading-icon").hide()
    this.$("#document-container").show()
    if (!this.fileId) return this.showError("There was a problem uploading your document. Please refresh the page and try again.")
    if (!this.preview) return this.showError("The document preview could not load. You can retry or download the document.")
    this.preview.show(this.fileId, this.previewToken, this.previewOptions)
    this.$("#update-document, #download-document").show()
  }

  showError(message) {
    this.$(".loading-icon").hide()
    this.$("#document-container").show()
    this.$("#upload-error").text(message).show()
    this.$("#upload-button").prop("disabled", false).text("Upload")
    this.$("#upload-progress").hide()
  }

  setLoading(loading) {
    this.$("#upload-button").prop("disabled", loading)
    this.$("#document-container").toggle(!loading)
    this.$(".loading-icon").toggle(loading)
  }

  resetUploader(event) {
    event.preventDefault()
    this.$(this.container).html(this.containerHTML)
    this.$("#update-document").hide()
  }

  downloadDocument(event) {
    event.preventDefault()
    if (typeof this.preview?.download !== "function") {
      this.$("#download-document").hide()
      return
    }
    this.preview.download()
  }

  updateSubmissionState() {
    const previousFileId = this.fileId
    this.fileId = this.submission.get("box_file_id")
    this.url = this.submission.get("url")
    this.updateSubmissionForm()
    if (this.fileId && this.fileId !== previousFileId) this.showPreview()
  }

  updateSubmissionForm() {
    this.$("#submission-form").attr("action", this.submission.url())
    this.$("#submit-button").prop("disabled", false)
  }

  cleanInput(_event, textarea) {
    window.setTimeout(() => {
      const value = String(textarea.value || "")
        .replace(/\.(\w{2,})/g, ". $1")
        .replace(/\n/g, " ")
        .replace(/\s{2,}/g, " ")
        .trim()
      textarea.value = value
    }, 10)
  }
}

class AdminDocumentView extends Widget {
  constructor(options = {}) {
    super()
    this.fileId = options.fileId
    this.documentResource = options.document
    this.pageOffset = options.pageOffset || 0
    this.uploadToken = options.uploadToken
    this.previewToken = options.previewToken
    this.previewOptions = {
      container: "#document-container",
      header: "none",
      showAnnotations: false,
      showDownload: false,
      ...(options.previewOptions || {})
    }
    this.container = this.previewOptions.container
    this.containerHTML = this.$(this.container).html()
    this.preview = createBoxPreview()
    if (this.documentResource) {
      this.fileId = this.documentResource.get("box_file_id")
      this.documentResource.on("change:id", this.updateDocumentForm, this)
    }
    this.bindEvents()
    if (this.fileId) this.showPreview()
  }

  bindEvents() {
    this.delegate("click", "#upload-button", this.triggerFileDialog)
    this.delegate("change", "#file-input", this.uploadDocument)
    this.delegate("click", "#update-document", this.resetUploader)
    this.delegate("click", "#download-document", this.downloadDocument)
    this.delegate("click", "#toc-close", this.closeTOC)
    this.delegate("click", "#toc-toggle", this.toggleTOC)
    this.delegate("click", ".toc-entry", this.navigateDocument)
  }

  setFileId(fileId) {
    this.fileId = fileId
    if (fileId) this.showPreview()
  }

  triggerFileDialog(event) {
    event.preventDefault()
    this.$("#file-input").trigger("click")
  }

  async uploadDocument(_event, input) {
    const file = input.files?.[0]
    if (!file) return
    if (file.type !== "application/pdf") return this.showError("Unsupported format. Please choose a PDF file.")
    if (file.size > 104_857_600) return this.showError("File size too large. Please select a document under 100MB.")
    this.$("#upload-error").hide()
    this.$("#upload-button").prop("disabled", true).text("Please wait...")
    this.$("#upload-progress").show()

    try {
      const response = await fetch("https://upload.box.com/api/2.0/files/content", {
        method: "POST",
        headers: { Authorization: `Bearer ${this.uploadToken}` },
        body: new FormData(document.querySelector("#upload-form"))
      })
      if (!response.ok) throw new Error(`Upload failed (${response.status})`)
      const payload = await response.json()
      this.file = payload.entries?.[0]
      this.fileId = this.file?.id
      if (!this.fileId) throw new Error("Box did not return a file id")
      await this.documentResource?.save({ box_file_id: this.fileId, filename: this.file.name })
      this.showPreview()
      this.updateDocumentForm()
    } catch (_) {
      this.showError("There was a problem uploading your document. Please refresh the page and try again.")
    }
  }

  showPreview() {
    if (!this.fileId || !this.preview) return this.showError("The document preview could not load. Please retry or download the PDF.")
    this.preview.show(this.fileId, this.previewToken, this.previewOptions)
    this.$("#update-document, #download-document").show()
    this.$("#upload-progress").hide()
  }

  showError(message) {
    const uploadError = this.$("#upload-error")
    if (uploadError.length) {
      uploadError.text(message).show()
    } else {
      const container = document.querySelector(this.container)
      if (container) {
        const alert = document.createElement("div")
        alert.className = "alert alert-danger"
        alert.setAttribute("role", "alert")
        alert.textContent = message
        container.replaceChildren(alert)
      }
    }
    this.$("#upload-button").prop("disabled", false).text("Upload")
    this.$("#upload-progress").hide()
  }

  resetUploader(event) {
    event.preventDefault()
    this.$(this.container).html(this.containerHTML)
    this.$("#update-document").hide()
  }

  downloadDocument(event) {
    event.preventDefault()
    if (typeof this.preview?.download !== "function") {
      this.$("#download-document").hide()
      return
    }
    this.preview.download()
  }

  updateDocumentForm() {
    this.$("#document-form").attr("action", this.documentResource.url())
    const name = this.$("#document_name")
    if (this.file && !name.val()) name.val(this.file.name)
    name.trigger("focus").trigger("select")
    this.$("#submit-button").prop("disabled", false)
  }

  closeTOC() {
    this.$("#toc").removeClass("visible")
  }

  toggleTOC() {
    this.$("#toc").toggleClass("visible")
  }

  navigateDocument(event, entry) {
    event.preventDefault()
    const page = Number($(entry).data("page"))
    this.preview?.viewer?.setPage(Number(this.pageOffset) + page)
    this.closeTOC()
  }
}

Object.assign(window, { DocumentView, AdminDocumentView })
